import { useEffect, useRef } from 'react';
import { Avatar } from '../../components/Avatar';
import { IconClap, IconHand, IconMicOff, IconPin, IconPresenter, IconScreen, IconThumbDown, IconThumbUp } from '../../components/icons';
import { useCall } from './CallProvider';
import type { Participant, Reaction, TrackSlot } from './callRoom';
import { REMOTE_MEDIA_CLASS } from './devices';

/*
 * Плитка участника: видео или фото, имя, значки состояния, рамка говорящего.
 * Одна и та же для меня и для собеседника, для сетки, полосы и свёрнутого
 * окошка — меняется только размер фотографии.
 */

/** Крупная плитка сетки, средняя в сетке на много человек, маленькая в полосе. */
export type TileSize = 'lg' | 'md' | 'sm';

const FACE: Record<TileSize, number> = { lg: 112, md: 64, sm: 40 };

export function Tile({
  person,
  userId,
  source,
  video,
  mine,
  micOn,
  hand,
  speaking,
  mirror,
  size = 'lg',
  hideName,
  fit,
  presenter,
  pinned,
  host,
  reaction,
  admin,
  caption,
  dim,
  onPick,
  backdrop,
  onAspect,
}: {
  person: { login: string; name: string };
  /** Чью дорожку показывать. */
  userId: string;
  /** Камера участника или его экран. */
  source: 'camera' | 'screen';
  /** Показывать видео (дорожка есть и камера включена). */
  video: boolean;
  mine: boolean;
  micOn: boolean;
  hand: boolean;
  speaking: boolean;
  /** Зеркалить своё видео (фронтальная камера). */
  mirror?: boolean;
  /** Размер плитки: от него зависит только величина фотографии. */
  size?: TileSize;
  /** Плитка без подписи — своё окошко и свёрнутый звонок. */
  hideName?: boolean;
  /** Как вписывать видео: обрезать (по умолчанию) или вписывать целиком (экран). */
  fit?: 'cover' | 'contain';
  /** Показывает экран сейчас — значок в бейдже с именем. */
  presenter?: boolean;
  /** Закреплён смотрящим: булавка в бейдже (макет Ильи 11.09.2026). */
  pinned?: boolean;
  /** Ведущий звонка: шильдик у имени; показ экрана и доска — его право. */
  host?: boolean;
  /** Реакция участника: значок без имени, гаснет сам (решение Ильи 11.09.2026). */
  reaction?: Reaction;
  /** Администратор звонка — словом в подсказке бейджа, без своего значка. */
  admin?: boolean;
  /** Приписка к имени: «звонит…» у того, кому ещё звонят. */
  caption?: string;
  /** Приглушить плитку — человек ещё не ответил. */
  dim?: boolean;
  /** Нажатие по плитке: администратор открывает ею меню участника. */
  onPick?: (anchor: HTMLElement) => void;
  /** Заполнять поля вокруг видео размытой копией кадра (сетка и главная плитка). */
  backdrop?: boolean;
  /** Сообщить фактическую пропорцию кадра — по ней раскладка выбирает форму плитки. */
  onAspect?: (aspect: number) => void;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  // дорожку плитка берёт сама: колбэк из родителя пересоздавался бы каждый
  // кадр таймера звонка и перецеплял видео раз в секунду
  const { attach, trackKeys } = useCall();
  const key = trackKeys[`${userId}:${source}`];

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return attach(el, userId, source);
  }, [attach, userId, source, key]);

  // Пропорция кадра — от самой дорожки, а не от типа устройства: телефон в
  // альбомной ориентации отдаёт горизонтальное видео, и наоборот. Размер
  // приходит с метаданными и потом меняется на ходу (поворот, смена камеры),
  // поэтому слушаем оба события.
  useEffect(() => {
    const el = ref.current;
    if (!el || !onAspect) return;
    const read = () => {
      if (el.videoWidth > 0 && el.videoHeight > 0) onAspect(el.videoWidth / el.videoHeight);
    };
    read();
    el.addEventListener('loadedmetadata', read);
    el.addEventListener('resize', read);
    return () => {
      el.removeEventListener('loadedmetadata', read);
      el.removeEventListener('resize', read);
    };
  }, [onAspect, userId, source, key]);

  // Поля вокруг видео заполняет размытая копия того же кадра — приём Google
  // Meet (решение Ильи 09.09.2026). Плитки при этом остаются одинаковыми:
  // плитка по формату дорожки перестраивала сетку на каждый вход и на каждый
  // поворот телефона. Своего потока подложке не нужно — та же дорожка
  // цепляется вторым элементом.
  const bgRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = bgRef.current;
    if (!el || !backdrop) return;
    return attach(el, userId, source);
  }, [attach, backdrop, userId, source, key]);

  const label = mine ? 'Вы' : person.name;

  return (
    <div
      className={`tile${speaking ? ' speaking' : ''}${mirror && mine ? ' mirror' : ''}${
        fit === 'contain' ? ' contain' : ''
      }${dim ? ' dim' : ''}`}
      onClick={onPick ? (e) => onPick(e.currentTarget) : undefined}
    >
      {backdrop && (
        <video ref={bgRef} className={`tile-bg${video ? '' : ' hidden'}`} autoPlay playsInline muted aria-hidden />
      )}
      {/* видеоэлемент живёт всегда: дорожка цепляется к нему и до включения камеры */}
      <video
        ref={ref}
        className={`tile-video${video ? '' : ' hidden'}${mine ? '' : ` ${REMOTE_MEDIA_CLASS}`}`}
        autoPlay
        playsInline
        muted={mine}
      />
      {!video && (
        <div className="tile-face">
          <Avatar login={person.login} name={person.name} size={FACE[size]} />
        </div>
      )}
      {!hideName && (
        <div className="tile-name" title={admin ? `${label} · администратор` : label}>
          {presenter && <IconScreen />}
          {host && <IconPresenter />}
          {pinned && <IconPin />}
          {!micOn && (
            <span className="tile-mic-off">
              <IconMicOff />
            </span>
          )}
          <span>{label}</span>
          {caption && <i>{caption}</i>}
        </div>
      )}
      {/* Рука — просьба взять слово: висит до опускания и подписана словами.
          Реакции рисуются не здесь, а одной плашкой сверху сцены (решение
          Ильи 11.09.2026); новая реакция руку опускает. */}
      {hand && (
        <div className="tile-hand" title="Поднята рука">
          <IconHand />
          <span>поднял руку</span>
        </div>
      )}
      {/* Реакция живёт на самой плитке и без имени: имя и так подписано
          (решение Ильи 11.09.2026). На компьютере — правый верхний угол, на
          телефоне — сверху по центру. */}
      {reaction && (
        <div className="tile-reaction" aria-hidden>
          {reaction === 'up' ? <IconThumbUp /> : reaction === 'down' ? <IconThumbDown /> : <IconClap />}
        </div>
      )}
    </div>
  );
}

/**
 * Плитка по участнику звонка: сетка, полоса и свёрнутое окошко берут
 * состояние из одного места, а не пересобирают одни и те же пропсы трижды.
 */
export function ParticipantTile({
  p,
  source = 'camera',
  size,
  hideName,
  fit,
  onPick,
  backdrop,
  pinned,
  onAspect,
}: {
  p: Participant;
  source?: Exclude<TrackSlot, 'audio'>;
  size?: TileSize;
  hideName?: boolean;
  fit?: 'cover' | 'contain';
  onPick?: (anchor: HTMLElement) => void;
  backdrop?: boolean;
  /** Закреплён смотрящим: булавка в бейдже. */
  pinned?: boolean;
  /** Сообщить пропорцию кадра раскладке. */
  onAspect?: (aspect: number) => void;
}) {
  const call = useCall();
  const ringing = p.status === 'ringing';
  return (
    <Tile
      person={p}
      userId={p.id}
      source={source}
      video={source === 'screen' ? p.screenOn : p.camOn && !ringing}
      mine={p.isMe}
      micOn={p.micOn}
      hand={p.hand}
      speaking={p.speaking}
      mirror={p.isMe ? call.mirror : undefined}
      size={size}
      hideName={hideName}
      fit={fit}
      presenter={p.screenOn}
      pinned={pinned}
      host={call.boardHostId === p.id}
      reaction={call.reaction?.from === p.id ? call.reaction.emo : undefined}
      admin={call.adminId === p.id}
      caption={ringing ? 'звонит…' : p.id.startsWith('guest:') ? 'гость' : undefined}
      dim={ringing}
      onPick={onPick}
      backdrop={backdrop}
      onAspect={onAspect}
    />
  );
}
